/**
 * Shared-funder corridor card (Analysis tab).
 *
 * Shows what the corridor review actually found — candidate shared upstream
 * funders (token units, never USD), energy delegation facts, pass-through
 * behaviour — plus the AI playbook draft when the model produced one. The card
 * never computes or adds risk; scoring belongs to the backend signal assembler.
 *
 * Also exports the small helpers every AI surface in the Workbench uses so the
 * "AI unavailable" and provenance rules live in one place:
 *   - isAiUnavailable / aiErrorText — honest failure detection
 *   - corridorStatus                 — success / no_evidence / unavailable
 *   - caseScopedErrorNotice          — 400 (no case) / 403 (feature off) wording
 *   - AiProvenanceLine               — "AI-generated, model X, id Y, requires human validation"
 *   - buildAiTraceElements           — AI-trace result → canvas elements (edge_type 'trace')
 */
import React, { useEffect, useRef, useState } from 'react';
import { ShieldAlert, Zap, ArrowRightLeft, Network, AlertTriangle, FileText, CheckCircle, Copy, Loader2, Play, Info } from 'lucide-react';
import { investigationAnalysisService } from '../../services/api';
import { computeEdgeLabel, normalizeCanvasAddr } from '../../utils/canvasSync';
import type { AiMeta, MacroAiTraceEdgeView } from '../NodeInspector/types';
import clsx from 'clsx';

// ─────────────────────────────────────────────────────────────────────────────
// Shared AI-result helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Legacy backends returned the failure as the text itself with status "success". */
const LEGACY_FAILURE_TEXT = /^\s*(?:⚠️?\s*)?(?:AI Analysis Unavailable|Syndicate Playbook Generation Failed)/i;

/**
 * True when an AI response must be shown as "AI unavailable" rather than as
 * text: explicit `status: "unavailable"`, a null/empty text field, or a legacy
 * failure string masquerading as output.
 */
export function isAiUnavailable(res: unknown, textKey: 'playbook' | 'analysis'): boolean {
    if (!res || typeof res !== 'object') return true;
    const r = res as Record<string, unknown>;
    if (String(r.status ?? '').toLowerCase() === 'unavailable') return true;
    const text = r[textKey];
    if (typeof text !== 'string' || !text.trim()) return true;
    return LEGACY_FAILURE_TEXT.test(text);
}

/** Human-readable reason for an unavailable AI response. */
export function aiErrorText(res: unknown, textKey: 'playbook' | 'analysis'): string {
    const r = (res && typeof res === 'object' ? res : {}) as Record<string, unknown>;
    if (typeof r.ai_error === 'string' && r.ai_error.trim()) return r.ai_error.trim();
    const text = r[textKey];
    if (typeof text === 'string' && LEGACY_FAILURE_TEXT.test(text)) {
        return text.replace(/^\s*⚠️?\s*/, '').trim();
    }
    return 'the AI model is not configured or returned no output';
}

/** Read `ai_meta` defensively (absent on older backends); a top-level `generation_id` fills a gap. */
export function readAiMeta(res: unknown): AiMeta | null {
    const r = (res && typeof res === 'object' ? res : {}) as Record<string, unknown>;
    const m = r.ai_meta && typeof r.ai_meta === 'object' ? (r.ai_meta as AiMeta) : null;
    const topId = typeof r.generation_id === 'string' && r.generation_id ? r.generation_id : null;
    if (!m) return topId ? { generation_id: topId } : null;
    return !m.generation_id && topId ? { ...m, generation_id: topId } : m;
}

export type CorridorStatus = 'success' | 'no_evidence' | 'unavailable';

/**
 * Corridor route outcome. `no_evidence` is a deterministic answer (no model was
 * called) and must read as neutral, never as "AI unavailable".
 */
export function corridorStatus(res: unknown): CorridorStatus {
    const r = (res && typeof res === 'object' ? res : {}) as Record<string, unknown>;
    if (String(r.status ?? '').toLowerCase() === 'no_evidence') return 'no_evidence';
    return isAiUnavailable(res, 'playbook') ? 'unavailable' : 'success';
}

/** The fixed `message` of a `no_evidence` corridor answer. */
export function corridorNoEvidenceMessage(res: unknown): string {
    const r = (res && typeof res === 'object' ? res : {}) as Record<string, unknown>;
    return typeof r.message === 'string' && r.message.trim()
        ? r.message.trim()
        : 'the trace found no candidate funder, delegation, pass-through or loop to review';
}

/**
 * Wording for the case-scoped AI routes: 400 = no valid case id sent, 403 = the
 * feature flag (AI_TRACE_ENABLED) is off. Anything else is a plain failure.
 */
export function caseScopedErrorNotice(err: any, action: string): { kind: 'disabled' | 'no_case' | 'error'; message: string } {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    if (status === 403) {
        return { kind: 'disabled', message: `${action} is switched off on this server (feature flag). Ask an administrator to enable it.` };
    }
    if (status === 400) {
        return { kind: 'no_case', message: `${action} needs an open case: ${typeof detail === 'string' ? detail : 'a valid case id was not sent'}.` };
    }
    return { kind: 'error', message: `${action} failed: ${typeof detail === 'string' ? detail : (err?.message || 'server error')}` };
}

export const CASE_REQUIRED_REASON = 'Open a case first';

/** Provenance text for AI output. Model and id are shown only when the backend sent them. */
export function aiProvenanceText(meta: AiMeta | null | undefined): string {
    if (meta && meta.llm_used === false) {
        return 'Heuristic fallback (no AI model used); requires human validation';
    }
    const parts = ['AI-generated'];
    if (meta?.model) parts.push(`model ${meta.model}`);
    if (meta?.generation_id) parts.push(`id ${meta.generation_id}`);
    parts.push('requires human validation');
    return parts.join(', ');
}

export const AiProvenanceLine: React.FC<{ meta?: AiMeta | null; className?: string }> = ({ meta, className }) => (
    <p
        className={clsx('text-[10px] text-muted-foreground italic leading-snug', className)}
        data-testid="ai-provenance"
    >
        {aiProvenanceText(meta)}
    </p>
);

/** Muted "AI unavailable" notice — deliberately not styled as a result. */
export const AiUnavailableNotice: React.FC<{ error?: string | null; className?: string }> = ({ error, className }) => (
    <div
        className={clsx('flex items-start gap-1.5 rounded-lg border border-dashed border-border bg-muted/30 px-2.5 py-2 text-[11px] text-muted-foreground', className)}
        data-testid="ai-unavailable"
    >
        <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
        <span>{aiUnavailableText(error)}</span>
    </div>
);

/**
 * Wording for an unavailable AI answer. A data-residency refusal (the
 * provider call was never made because LLM_PII_POLICY withheld it) is a
 * neutral statement about where the data stays, not a failure.
 */
export const RESIDENCY_NOTICE = 'AI text is not generated here: case data stays on this server';
export function aiUnavailableText(error?: string | null): string {
    if (error && /pii_policy/i.test(error)) return RESIDENCY_NOTICE;
    return `AI unavailable: ${error || 'the AI model is not configured or returned no output'}`;
}

/** Neutral "nothing to review" answer from the corridor route (no model was called). */
export const CorridorNoEvidenceNotice: React.FC<{ message?: string | null; className?: string }> = ({ message, className }) => (
    <div
        className={clsx('flex items-start gap-1.5 rounded-lg border border-border bg-muted/20 px-2.5 py-2 text-[11px] text-foreground/90', className)}
        data-testid="corridor-no-evidence"
    >
        <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-muted-foreground" />
        <span>No corridor evidence for this address: {message || 'the trace found nothing to review'}</span>
    </div>
);

/** Placeholder names the AI tracer uses when no entity is known — never shown as attribution. */
const PLACEHOLDER_ENTITY_NAMES = new Set(['unlabeled', 'unlabelled', 'unknown', 'feeder conduit', 'layering relay']);

function shortAddr(a: string): string {
    return a.length > 14 ? `${a.slice(0, 6)}…${a.slice(-4)}` : a;
}

function numOrNull(v: unknown): number | null {
    const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
    return Number.isFinite(n) ? n : null;
}

function strOrNull(v: unknown): string | null {
    return typeof v === 'string' && v.trim() ? v.trim() : null;
}

export interface AiTraceElementsResult {
    elements: Array<{ data: Record<string, unknown> }>;
    nodeCount: number;
    edgeCount: number;
    selectionMethods: Record<string, number>;
    edges: MacroAiTraceEdgeView[];
}

/**
 * Map an AI-trace result onto canvas elements. Declares intent only (R23/R24):
 * edges are `edge_type: 'trace'` with `selection_method` + `ai_generation_id`
 * so the stylesheet can badge them; nodes get a `placement_role` from the hop
 * sign. No colours, widths, classes, coordinates or `_visibility` are written.
 */
export function buildAiTraceElements(trace: unknown, chain: string, aiMeta?: AiMeta | null): AiTraceElementsResult {
    const t = (trace && typeof trace === 'object' ? trace : {}) as Record<string, unknown>;
    const rawNodes = Array.isArray(t.nodes) ? (t.nodes as Record<string, unknown>[]) : [];
    const rawEdges = Array.isArray(t.edges) ? (t.edges as Record<string, unknown>[]) : [];
    const generationId = aiMeta?.generation_id || null;
    const elements: Array<{ data: Record<string, unknown> }> = [];
    const nodeIds = new Set<string>();

    for (const n of rawNodes) {
        const addr = strOrNull(n.address) || strOrNull(n.id);
        if (!addr) continue;
        const id = normalizeCanvasAddr(addr);
        if (nodeIds.has(id)) continue;
        nodeIds.add(id);
        const hop = numOrNull(n.hop);
        const placementRole = hop === null ? 'context' : hop === 0 ? 'subject' : hop < 0 ? 'source' : 'sink';
        const entity = strOrNull(n.entity);
        const realEntity = entity && !PLACEHOLDER_ENTITY_NAMES.has(entity.toLowerCase()) ? entity : null;
        elements.push({
            data: {
                id,
                label: realEntity || shortAddr(id),
                type: 'address',
                chain,
                placement_role: placementRole,
                discovered_via: 'ai_trace',
                ...(hop !== null ? { hop } : {}),
                ...(realEntity ? { entity_name: realEntity } : {}),
                ...(generationId ? { ai_generation_id: generationId } : {}),
            },
        });
    }

    const selectionMethods: Record<string, number> = {};
    const edges: MacroAiTraceEdgeView[] = [];
    for (const e of rawEdges) {
        const src = strOrNull(e.source);
        const tgt = strOrNull(e.target);
        if (!src || !tgt) continue;
        const source = normalizeCanvasAddr(src);
        const target = normalizeCanvasAddr(tgt);
        const txHash = strOrNull(e.tx_hash);
        const value = numOrNull(e.value);
        const token = strOrNull(e.token_symbol);
        const method = strOrNull(e.selection_method) || 'unspecified';
        selectionMethods[method] = (selectionMethods[method] || 0) + 1;
        edges.push({ source, target, value, token_symbol: token, tx_hash: txHash, selection_method: method });
        const baseId = `${source}->${target}`;
        elements.push({
            data: {
                id: txHash ? `${baseId}#${txHash}` : baseId,
                source,
                target,
                edge_type: 'trace',
                selection_method: method,
                discovered_via: 'ai_trace',
                ...(value !== null ? { value } : {}),
                ...(token ? { token_symbol: token } : {}),
                ...(txHash ? { tx_hash: txHash } : {}),
                ...(generationId ? { ai_generation_id: generationId } : {}),
                label: computeEdgeLabel(value, token || undefined) || undefined,
            },
        });
    }

    return { elements, nodeCount: nodeIds.size, edgeCount: edges.length, selectionMethods, edges };
}

// ─────────────────────────────────────────────────────────────────────────────
// Formatting
// ─────────────────────────────────────────────────────────────────────────────

/** "12,345 USDT" — token units only; never a currency symbol. */
export function formatTokenAmount(value: unknown, token?: string | null): string {
    const n = numOrNull(value);
    if (n === null) return '—';
    const amount = n.toLocaleString('en-US', { maximumFractionDigits: 2 });
    return token ? `${amount} ${token}` : amount;
}

function formatDuration(seconds: unknown): string | null {
    const s = numOrNull(seconds);
    if (s === null || s < 0) return null;
    if (s < 120) return `${Math.round(s)} s`;
    if (s < 7200) return `${Math.round(s / 60)} min`;
    return `${(s / 3600).toFixed(1)} h`;
}

function formatPct(v: unknown): string | null {
    const n = numOrNull(v);
    return n === null ? null : `${(n * 100).toFixed(0)}%`;
}

/** Render backend evidence of unknown shape (bool / string / list / object) as plain text. */
function evidenceText(v: unknown): string | null {
    if (v === null || v === undefined || v === '') return null;
    // Energy detector shape: { checked, found, matches }.
    if (typeof v === 'object' && !Array.isArray(v) && 'checked' in (v as Record<string, unknown>)) {
        const o = v as { checked?: unknown; found?: unknown; matches?: unknown };
        if (o.checked === false) return 'not checked';
        const n = Array.isArray(o.matches) ? o.matches.length : 0;
        if (o.found === true) return `found${n ? ` (${n} matching payment${n === 1 ? '' : 's'})` : ''}`;
        if (o.found === false) return 'none found';
        return 'not determined';
    }
    if (typeof v === 'boolean') return v ? 'yes' : 'none found';
    if (typeof v === 'number' || typeof v === 'string') return String(v);
    if (Array.isArray(v)) {
        if (v.length === 0) return 'none found';
        const head = v.slice(0, 3).map((x) => (typeof x === 'object' ? JSON.stringify(x) : String(x)));
        return `${v.length} item(s): ${head.join('; ')}${v.length > 3 ? '; …' : ''}`;
    }
    if (typeof v === 'object') {
        const entries = Object.entries(v as Record<string, unknown>).filter(([, x]) => x !== null && x !== undefined && x !== '');
        if (entries.length === 0) return 'none found';
        return entries.slice(0, 6).map(([k, x]) => `${k.replace(/_/g, ' ')}: ${typeof x === 'object' ? JSON.stringify(x) : String(x)}`).join(' · ');
    }
    return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Card
// ─────────────────────────────────────────────────────────────────────────────

interface SyndicateCorridorCardProps {
    chain?: string | null;
    address?: string | null;
    /** Case currently open in the Workbench — required by the AI-assisted trace. */
    caseId?: string | null;
    onNotice?: (msg: string, type: 'info' | 'success' | 'warning' | 'error') => void;
}

type CardResult =
    | { kind: 'corridor'; address: string; data: any; status: CorridorStatus; error: string | null; message: string | null; aiMeta: AiMeta | null }
    | { kind: 'ai-trace'; address: string; trace: any; aiMeta: AiMeta | null; view: AiTraceElementsResult };

export const SyndicateCorridorCard: React.FC<SyndicateCorridorCardProps> = ({
    chain = 'TRX',
    address,
    caseId,
    onNotice,
}) => {
    const [targetWallet, setTargetWallet] = useState(address || '');
    const [loading, setLoading] = useState(false);
    const [activeAction, setActiveAction] = useState<'corridor' | 'ai-trace' | null>(null);
    const [result, setResult] = useState<CardResult | null>(null);
    const [copied, setCopied] = useState(false);
    // R8 stale-response guard: every run and every address change bumps the id;
    // a response whose id is no longer current is dropped.
    const requestIdRef = useRef(0);

    useEffect(() => {
        requestIdRef.current += 1;
        if (address) setTargetWallet(address);
        setResult(null);
        setLoading(false);
        setActiveAction(null);
        setCopied(false);
    }, [address]);

    useEffect(() => () => { requestIdRef.current += 1; }, []);

    const activeChain = chain || 'TRX';
    // Both AI routes are case-scoped (400 without a case id), so both actions
    // are disabled until a case is open.
    const caseBlockedReason = caseId ? null : CASE_REQUIRED_REASON;

    const handleRunCorridor = async () => {
        const target = targetWallet.trim();
        if (!target) {
            onNotice?.('Please select or enter a wallet address.', 'warning');
            return;
        }
        if (!caseId) {
            onNotice?.(`${CASE_REQUIRED_REASON}: the corridor review is case-scoped.`, 'warning');
            return;
        }
        const reqId = ++requestIdRef.current;
        setLoading(true);
        setActiveAction('corridor');
        setResult(null);
        try {
            const data = await investigationAnalysisService.analyzeSyndicateCorridor(activeChain, target, { caseId });
            if (reqId !== requestIdRef.current) return;
            const status = corridorStatus(data);
            setResult({
                kind: 'corridor',
                address: target,
                data,
                status,
                error: status === 'unavailable' ? aiErrorText(data, 'playbook') : null,
                message: status === 'no_evidence' ? corridorNoEvidenceMessage(data) : null,
                aiMeta: readAiMeta(data),
            });
            if (status === 'unavailable') {
                onNotice?.(`Corridor data loaded. AI playbook unavailable: ${aiErrorText(data, 'playbook')}`, 'warning');
            } else if (status === 'no_evidence') {
                onNotice?.(`No corridor evidence for this address: ${corridorNoEvidenceMessage(data)}`, 'info');
            } else {
                onNotice?.('Corridor review complete. The playbook is an AI draft for human review.', 'info');
            }
        } catch (err: any) {
            if (reqId !== requestIdRef.current) return;
            const n = caseScopedErrorNotice(err, 'The corridor review');
            onNotice?.(n.message, n.kind === 'error' ? 'error' : 'warning');
        } finally {
            if (reqId === requestIdRef.current) {
                setLoading(false);
                setActiveAction(null);
            }
        }
    };

    const handleRunAITrace = async () => {
        const target = targetWallet.trim();
        if (!target) {
            onNotice?.('Please select or enter a wallet address.', 'warning');
            return;
        }
        if (!caseId) {
            onNotice?.(`${CASE_REQUIRED_REASON}: the AI-assisted trace is case-scoped.`, 'warning');
            return;
        }
        const reqId = ++requestIdRef.current;
        setLoading(true);
        setActiveAction('ai-trace');
        setResult(null);
        try {
            const data = await investigationAnalysisService.aiTrace(activeChain, target, { caseId, maxForwardDepth: 2, maxBackwardDepth: 2 });
            if (reqId !== requestIdRef.current) return;
            const aiMeta = readAiMeta(data);
            const trace = data?.trace ?? null;
            setResult({ kind: 'ai-trace', address: target, trace, aiMeta, view: buildAiTraceElements(trace, activeChain, aiMeta) });
            onNotice?.('AI-assisted trace finished. Branches are leads for verification.', 'info');
        } catch (err: any) {
            if (reqId !== requestIdRef.current) return;
            const n = caseScopedErrorNotice(err, 'The AI-assisted trace');
            onNotice?.(n.message, n.kind === 'error' ? 'error' : 'warning');
        } finally {
            if (reqId === requestIdRef.current) {
                setLoading(false);
                setActiveAction(null);
            }
        }
    };

    const playbook = result?.kind === 'corridor' && result.status === 'success' ? String(result.data?.playbook || '') : '';

    const handleCopyPlaybook = async () => {
        if (!playbook) return;
        const clip = typeof navigator !== 'undefined' ? navigator.clipboard : undefined;
        if (!clip?.writeText) {
            onNotice?.('Clipboard is not available in this browser context. Select the text to copy it.', 'warning');
            return;
        }
        try {
            await clip.writeText(playbook);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
            onNotice?.('Playbook draft copied to clipboard.', 'success');
        } catch {
            onNotice?.('Copy failed. Select the text to copy it.', 'warning');
        }
    };

    const syndicateData = result?.kind === 'corridor' ? result.data?.syndicate_data : result?.kind === 'ai-trace' ? result.trace : null;
    const energy = syndicateData?.energy_sponsorship;
    // `conduit_analysis` is the canonical key; `conduit_metrics` only on older backends.
    const conduit = syndicateData?.conduit_analysis ?? syndicateData?.conduit_metrics;
    const clusters: any[] = Array.isArray(syndicateData?.syndicate_clusters) ? syndicateData.syndicate_clusters : [];
    const secondaryScams: any[] = Array.isArray(syndicateData?.secondary_scam_alerts) ? syndicateData.secondary_scam_alerts : [];
    const loops: any[] = Array.isArray(syndicateData?.hawala_loops) ? syndicateData.hawala_loops : [];

    const delegatorCount = numOrNull(energy?.delegator_count) ?? (Array.isArray(energy?.delegators) ? energy.delegators.length : 0);
    const delegatedSun = numOrNull(energy?.delegated_energy_sun) ?? 0;
    // R10: triggered === null means the check could not run (NOT TESTED) — never
    // rendered as clear or as a hit. true = corroborated; false = ran.
    const energyNotTested = Boolean(energy) && (energy.triggered === null || energy.tested === false);
    const energyCorroborated = Boolean(energy) && !energyNotTested && energy.triggered === true;
    const energyObserved = Boolean(energy) && !energyNotTested && !energyCorroborated
        && (typeof energy.delegation_observed === 'boolean' ? energy.delegation_observed : (delegatorCount > 0 || delegatedSun > 0));
    const paidRental = evidenceText(energy?.paid_rental_evidence);
    const energyNote = typeof energy?.note === 'string' && energy.note.trim() ? energy.note.trim() : null;

    return (
        <div className="p-3 space-y-3 text-xs" data-testid="syndicate-corridor-card">
            {/* Input Bar */}
            <div className="space-y-1.5">
                <div className="flex items-center justify-between text-muted-foreground">
                    <span className="font-semibold text-foreground flex items-center gap-1.5">
                        <Network className="w-3.5 h-3.5 text-primary" />
                        Target address
                    </span>
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-muted/60">
                        {activeChain.toUpperCase()}
                    </span>
                </div>
                <div className="flex gap-1.5">
                    <input
                        type="text"
                        value={targetWallet}
                        onChange={(e) => setTargetWallet(e.target.value)}
                        placeholder="Enter TRX or EVM address..."
                        className="flex-1 bg-background border border-border/80 rounded-lg px-2.5 py-1.5 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                </div>
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-2 gap-2">
                <button
                    onClick={handleRunCorridor}
                    disabled={loading || !targetWallet.trim() || !caseId}
                    title={caseBlockedReason || 'Review upstream feeders for a candidate shared funder and draft a playbook for human review'}
                    data-testid="corridor-run"
                    className={clsx(
                        "flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg font-medium transition-all shadow-sm disabled:opacity-60",
                        loading && activeAction === 'corridor'
                            ? "bg-primary/20 text-primary cursor-wait"
                            : "bg-primary text-primary-foreground hover:bg-primary/90"
                    )}
                >
                    {loading && activeAction === 'corridor' ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                        <ShieldAlert className="w-3.5 h-3.5" />
                    )}
                    <span>Corridor review & playbook</span>
                </button>

                <button
                    onClick={handleRunAITrace}
                    disabled={loading || !targetWallet.trim() || !caseId}
                    title={caseBlockedReason || 'Trace 2 hops back and 2 hops forward; branches are chosen from the actual candidate transactions'}
                    data-testid="corridor-ai-trace"
                    className={clsx(
                        "flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg font-medium border border-border/80 transition-all disabled:opacity-60",
                        loading && activeAction === 'ai-trace'
                            ? "bg-accent text-accent-foreground cursor-wait"
                            : "hover:bg-accent text-foreground"
                    )}
                >
                    {loading && activeAction === 'ai-trace' ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                        <Play className="w-3.5 h-3.5 text-primary" />
                    )}
                    <span>AI-assisted trace</span>
                </button>
            </div>
            {caseBlockedReason && (
                <p className="text-[10px] text-muted-foreground -mt-1" data-testid="corridor-case-required">
                    {caseBlockedReason}: both actions are case-scoped.
                </p>
            )}

            {/* Empty State */}
            {!result && !loading && (
                <div className="p-3 rounded-lg border border-dashed border-border/80 text-center text-muted-foreground">
                    <strong>Corridor review</strong> looks for a candidate shared upstream funder across this address's feeder wallets,
                    and reports energy delegation and pass-through behaviour as observed facts. Any playbook is an AI draft that requires human validation.
                </div>
            )}

            {/* Loading Indicator */}
            {loading && (
                <div className="p-4 rounded-lg bg-card/60 border border-border/80 flex flex-col items-center justify-center gap-2 text-muted-foreground">
                    <Loader2 className="w-5 h-5 animate-spin text-primary" />
                    <span className="font-medium text-xs">
                        {activeAction === 'corridor' ? 'Reviewing upstream feeders and drafting a playbook…' : 'Running the AI-assisted trace…'}
                    </span>
                </div>
            )}

            {/* Results Display */}
            {result && !loading && (
                <div className="space-y-2.5 pt-1" data-testid="corridor-result">
                    <div className="text-[10px] text-muted-foreground font-mono break-all">
                        Result for {result.address}
                    </div>

                    {/* Energy delegation check could not run — NOT TESTED, neither clear nor hit */}
                    {energyNotTested && (
                        <div className="p-2.5 rounded-lg border border-dashed border-border bg-muted/20 flex items-start gap-2" data-testid="corridor-energy-not-tested">
                            <Zap className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                            <div className="text-[11px] text-muted-foreground">
                                <span className="font-semibold text-foreground">Energy delegation: not tested</span>
                                {energy?.not_tested_reason ? ` (${String(energy.not_tested_reason)})` : ''}.
                            </div>
                        </div>
                    )}

                    {/* Delegation from a corroborated related party (backend triggered=true) */}
                    {energyCorroborated && (
                        <div className="p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-start gap-2" data-testid="corridor-energy-corroborated">
                            <Zap className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                            <div className="space-y-0.5">
                                <div className="font-semibold text-foreground">
                                    {typeof energy?.classification === 'string' && energy.classification ? energy.classification : 'Energy delegation from a corroborated related party'}
                                </div>
                                {energyNote && <div className="text-[11px] text-muted-foreground">{energyNote}</div>}
                                {formatPct(energy?.confidence) && (
                                    <div className="text-[10px] text-muted-foreground">Confidence: {formatPct(energy?.confidence)}</div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Energy delegation — a commercial service; reported as a fact, never as risk */}
                    {energyObserved && (
                        <div className="p-2.5 rounded-lg bg-muted/40 border border-border flex items-start gap-2" data-testid="corridor-energy">
                            <Zap className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                            <div className="space-y-0.5">
                                <div className="font-semibold text-foreground">
                                    Energy delegation observed (common commercial service)
                                </div>
                                <div className="text-[11px] text-muted-foreground">
                                    {delegatorCount} delegator{delegatorCount === 1 ? '' : 's'}
                                    {delegatedSun > 0 ? <>; delegated energy <strong>{delegatedSun.toLocaleString('en-US')} SUN</strong></> : null}.
                                </div>
                                {paidRental && (
                                    <div className="text-[11px] text-muted-foreground">Paid rental evidence: {paidRental}</div>
                                )}
                                {energyNote && <div className="text-[10px] text-muted-foreground">{energyNote}</div>}
                            </div>
                        </div>
                    )}

                    {/* Pass-through behaviour */}
                    {conduit?.is_conduit && (
                        <div className="p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-start gap-2" data-testid="corridor-conduit">
                            <ArrowRightLeft className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                            <div className="space-y-0.5">
                                <div className="font-semibold text-foreground">
                                    {typeof conduit.classification === 'string' && conduit.classification
                                        ? `Observed: ${conduit.classification}`
                                        : 'Pass-through pattern observed'}
                                </div>
                                <div className="text-[11px] text-muted-foreground">
                                    {formatPct(conduit.pass_through_ratio) ? <>Outflow/inflow ratio <strong>{formatPct(conduit.pass_through_ratio)}</strong></> : 'Outflow closely matches inflow'}
                                    {formatDuration(conduit.retention_seconds) ? <>, retention about <strong>{formatDuration(conduit.retention_seconds)}</strong></> : null}
                                    {conduit.primary_token ? <> ({String(conduit.primary_token)})</> : null}.
                                </div>
                                {formatPct(conduit.confidence) && (
                                    <div className="text-[10px] text-muted-foreground">Confidence: {formatPct(conduit.confidence)}</div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Candidate shared funder(s) */}
                    {clusters.length > 0 && (
                        <div className="p-2.5 rounded-lg bg-primary/5 border border-primary/25 space-y-1.5" data-testid="corridor-clusters">
                            <div className="flex items-center gap-1.5 font-semibold text-foreground">
                                <Network className="w-3.5 h-3.5 text-primary" />
                                <span>Candidate shared funder{clusters.length > 1 ? 's' : ''}</span>
                            </div>
                            {clusters.map((cl: any, idx: number) => {
                                const token = strOrNull(cl.primary_token);
                                const conf = formatPct(cl.confidence);
                                return (
                                    <div key={cl.cluster_id || idx} className="bg-background/80 p-2 rounded border border-border/50 text-[11px] space-y-1">
                                        <div className="font-medium text-foreground break-all">
                                            Funder: <span className="font-mono">{cl.root_supplier}</span>
                                            {cl.root_supplier_entity_name ? <span className="text-muted-foreground"> ({String(cl.root_supplier_entity_name)})</span> : null}
                                        </div>
                                        <div className="text-muted-foreground grid grid-cols-2 gap-1 text-[10px]">
                                            <div>Amount from funder: <strong>{formatTokenAmount(cl.supplier_injected_amount, token)}</strong></div>
                                            <div>Feeders: <strong>{cl.feeder_count ?? '—'}</strong></div>
                                            <div>Concentration: <strong>{formatPct(cl.concentration_ratio) ?? '—'}</strong></div>
                                            <div>Feeder inflow total: <strong>{formatTokenAmount(cl.aggregate_corridor_amount, token)}</strong></div>
                                            {conf && <div>Confidence: <strong>{conf}</strong></div>}
                                            {cl.typology && <div>Typology: <strong>{String(cl.typology).replace(/_/g, ' ')}</strong></div>}
                                        </div>
                                        {cl.recommended_action && (
                                            <div className="text-[10px] text-foreground/90">{String(cl.recommended_action)}</div>
                                        )}
                                        {cl.corroboration_required !== false && (
                                            <div className="flex items-start gap-1 text-[10px] text-amber-700 dark:text-amber-300" data-testid="corridor-corroboration">
                                                <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
                                                <span>Confirm it is not an exchange, bridge or payment service before any enforcement step.</span>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Possible closed-loop returns — leads for review, with both legs' evidence */}
                    {loops.length > 0 && (
                        <div className="p-2.5 rounded-lg bg-muted/40 border border-border space-y-1.5" data-testid="corridor-loops">
                            {loops.map((lp: any, idx: number) => {
                                const ev = lp?.evidence || {};
                                const token = strOrNull(ev.token);
                                const hours = numOrNull(ev.delta_seconds);
                                return (
                                    <div key={lp?.loop_id || idx} className="text-[11px] space-y-0.5">
                                        <div className="font-semibold text-foreground">
                                            {typeof lp?.classification === 'string' && lp.classification ? lp.classification : 'Possible closed-loop return (requires review)'}
                                        </div>
                                        <div className="text-muted-foreground">
                                            {lp?.intersection_node ? <span className="font-mono">{shortAddr(String(lp.intersection_node))}</span> : 'An address'}
                                            {' '}sent {formatTokenAmount(ev.value_out, token)} and received {formatTokenAmount(ev.value_in, token)} back
                                            {hours !== null ? ` ${(hours / 3600).toFixed(1)} h later` : ''}
                                            {Array.isArray(lp?.upstream_leg) || Array.isArray(lp?.downstream_leg)
                                                ? ` (${Array.isArray(lp?.upstream_leg) ? lp.upstream_leg.length : '?'} hop(s) out, ${Array.isArray(lp?.downstream_leg) ? lp.downstream_leg.length : '?'} back)`
                                                : ''}.
                                        </div>
                                        <div className="text-[10px] font-mono text-muted-foreground break-all">
                                            {ev.upstream_tx ? `out tx ${String(ev.upstream_tx).slice(0, 12)}…` : 'out tx —'} · {ev.downstream_tx ? `back tx ${String(ev.downstream_tx).slice(0, 12)}…` : 'back tx —'}
                                            {formatPct(lp?.confidence) ? ` · confidence ${formatPct(lp?.confidence)}` : ''}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Secondary recovery-scam warnings from the entity database */}
                    {secondaryScams.length > 0 && (
                        <div className="p-2.5 rounded-lg bg-red-500/10 border border-red-500/30 flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                            <div>
                                <div className="font-semibold text-foreground">
                                    Recovery-scam warning on a downstream address (complainant risk)
                                </div>
                                {secondaryScams.map((sc: any, idx: number) => (
                                    <div key={idx} className="text-[11px] text-foreground/90 mt-1">
                                        {sc.warning}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* AI-assisted trace summary */}
                    {result.kind === 'ai-trace' && (
                        <div className="p-2.5 rounded-lg bg-card border border-border/80 space-y-1.5" data-testid="corridor-ai-trace-result">
                            <div className="font-semibold text-foreground">
                                AI-assisted trace: {result.view.nodeCount} address{result.view.nodeCount === 1 ? '' : 'es'}, {result.view.edgeCount} edge{result.view.edgeCount === 1 ? '' : 's'}
                            </div>
                            {Object.keys(result.view.selectionMethods).length > 0 && (
                                <div className="text-[10px] text-muted-foreground">
                                    Selected by: {Object.entries(result.view.selectionMethods).map(([m, c]) => `${m.replace(/_/g, ' ')} (${c})`).join(', ')}
                                </div>
                            )}
                            {result.view.edges.slice(0, 8).map((e, i) => (
                                <div key={`${e.source}-${e.target}-${i}`} className="text-[10px] font-mono text-muted-foreground break-all">
                                    {shortAddr(e.source)} → {shortAddr(e.target)}
                                    {e.value !== null && e.value !== undefined ? ` · ${formatTokenAmount(e.value, e.token_symbol)}` : ''}
                                    {e.tx_hash ? ` · tx ${e.tx_hash.slice(0, 10)}…` : ' · no tx hash'}
                                </div>
                            ))}
                            <div className="text-[10px] text-muted-foreground">
                                Shown here only; these branches are not added to the canvas from this card.
                            </div>
                            <AiProvenanceLine meta={result.aiMeta} />
                        </div>
                    )}

                    {/* Playbook (AI draft), the neutral no-evidence answer, or the honest unavailable state */}
                    {result.kind === 'corridor' && result.status === 'no_evidence' && (
                        <CorridorNoEvidenceNotice message={result.message} />
                    )}
                    {result.kind === 'corridor' && result.status === 'unavailable' && (
                        <AiUnavailableNotice error={result.error} />
                    )}
                    {result.kind === 'corridor' && result.status === 'success' && playbook && (
                        <div className="p-2.5 rounded-lg bg-card border border-border/80 space-y-2" data-testid="corridor-playbook">
                            <div className="flex items-center justify-between border-b border-border/50 pb-1.5">
                                <div className="flex items-center gap-1.5 font-semibold text-foreground">
                                    <FileText className="w-3.5 h-3.5 text-primary" />
                                    <span>Playbook draft (AI)</span>
                                </div>
                                <button
                                    onClick={handleCopyPlaybook}
                                    data-testid="corridor-copy"
                                    className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded border border-border hover:bg-accent text-muted-foreground hover:text-foreground transition-all"
                                >
                                    {copied ? (
                                        <>
                                            <CheckCircle className="w-3 h-3 text-emerald-500" />
                                            <span>Copied</span>
                                        </>
                                    ) : (
                                        <>
                                            <Copy className="w-3 h-3" />
                                            <span>Copy</span>
                                        </>
                                    )}
                                </button>
                            </div>
                            <div className="max-h-[350px] overflow-y-auto styled-scrollbar text-[11px] leading-relaxed text-foreground/90 whitespace-pre-wrap font-sans bg-muted/20 p-2.5 rounded border border-border/40">
                                {playbook}
                            </div>
                            <AiProvenanceLine meta={result.aiMeta} />
                        </div>
                    )}

                    {result.kind === 'corridor' && result.status !== 'no_evidence' && clusters.length === 0 && !energyObserved && !energyCorroborated && !conduit?.is_conduit && loops.length === 0 && (
                        <div className="text-[11px] text-muted-foreground">
                            No candidate shared funder, energy delegation or pass-through pattern was found for this address.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
